import {
  CurrentHairColorKey,
  CurrentHairTypeKey,
  EyeColorKey,
  HairColorKey,
  HairTypeKey,
  PhysicalCharacteristicKey,
  SkinColorKey,
} from "@/enums/characteristic";
import { LanguageEnum, LanguageNameKey, LanguageProficiencyKey } from "@/enums/language";
import { ClothingSizeKey, ShoeSizeKey } from "@/enums/measurements";
import { KinshipKey } from "@/enums/responsible";
import { SkillCategoryEnum, SkillNameEnum } from "@/enums/skill";
import { FollowerRangeEnum, SocialMediaPlatformKey } from "@/enums/social_media";
import { ClosingLocationEnum, DisabilityTypeKey, TalentLogSectionEnum, TalentStatusEnum } from "@/enums/talent";
import { BaseModel } from "@/interfaces/api";
import { NationalityKey } from "./../../enums/talent";
import Photo from "../photo/models";
import { User } from "../user/models";

export interface ISocialMedia {
  id: string;
  plataform: SocialMediaPlatformKey;
  username: string;
  followerRange: FollowerRangeEnum;
}

export interface ILanguage {
  id: string;
  proficiencyLevel: LanguageProficiencyKey;
  name: LanguageNameKey;
}

export interface ISkill {
  id: string;
  name: SkillNameEnum;
  category: SkillCategoryEnum;
}

export interface IAttributeSkill {
  id: string;
  details: string | null;
  skill: ISkill;
}

export interface IAddress {
  id?: string;
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface AssociatedTalent {
  id: string;
  fullName: string;
  birthday: string;
  talentCode: string;
  profilePhotoUrl: string | null;
  scheduleId?: string;
}

export interface Responsible extends BaseModel {
  kinship: KinshipKey;
  fullName: string;
  phone: string;
  secondaryPhone?: string | null;
  email: string;
  birthday?: string | null;
  rg?: string | null;
  cpf: string;
  sameAddressAsTalent?: boolean;
  address?: IAddress | null;
  associatedTalents?: AssociatedTalent[];
}

export interface IAttribute {
  id?: string;
  languages: ILanguage[];
  attributeSkills: IAttributeSkill[];
  attributeSkillsToDelete?: string[];
  // Characteristics
  eyeColor?: EyeColorKey | null;
  naturalHairColor?: HairColorKey | null;
  currentHairColor?: CurrentHairColorKey | null;
  naturalHairType?: HairTypeKey | null;
  currentHairType?: CurrentHairTypeKey | null;
  skinColor?: SkinColorKey | null;
  physicalFeatures?: PhysicalCharacteristicKey[] | null;
  hasTattoo?: boolean | null;
  // Measurements
  height?: number | null;
  shoeSize?: ShoeSizeKey[] | null;
  clothingSize?: ClothingSizeKey[] | null;
}

export interface LogEntry {
  id: string;
  createdDate: string;
  section: TalentLogSectionEnum;
  authorName: string | null;
}

export interface TalentJobHistoryEntry {
  stage: string;
  createdDate: string;
  job: {
    name: string;
    jobType: string | null;
    castingTeam: string | null;
  };
}

/** Propriedades comuns para Talent e CreateTalent */
interface ITalentBase {
  closingLocation: ClosingLocationEnum;
  nationality?: NationalityKey | null;
  fullName: string;
  artisticName?: string | null;
  birthday: string;
  email?: string | null;
  rg: string;
  cpf?: string | null;
  genderIdentity?: string | null;
  hasBiologicalSibling?: boolean | null;
  isTwinSibling?: boolean | null;
  hasDisability?: boolean | null;
  disabilityType?: DisabilityTypeKey[] | null;
  hasPreviousWorkExperience?: boolean | null;
  isRepresentedByAnotherAgency?: boolean | null;
  hasPhotoMaterial?: boolean | null;
  hasDRT?: boolean | null;
  address: IAddress;
  phone?: string | null;
  secondaryPhone?: string | null;
  attribute?: IAttribute | null;
  responsibles: Responsible[];
  age?: string;
  picture?: {
    url: string;
  };
  observation?: string | null;
  archived: boolean;
  archivedDate?: string | null;
  archivedBy?: User | null;
  contractExpirationDate?: string | null;
  talentStatus: TalentStatusEnum;
}

export interface CreateTalent extends ITalentBase {
  profilePhotoUrl?: string | null;
  socialMedias: Omit<ISocialMedia, "id">[];
}

export interface Talent extends BaseModel, ITalentBase {
  talentCode: string;
  archivingReason?: string | null;
  // TODO: descomentar quando criar interface IDocument
  // documents: IDocument[];
  photos: Omit<Photo, "talent">[];
  socialMedias: ISocialMedia[];
  talentLogs: LogEntry[];
}

export enum TalentArchivingReasonEnum {
  CHANGE = "change",
  QUIT = "quit",
  EXPIRED_CONTRACT = "expired_contract",
  INACTIVE_STATUS = "inactive_status",
}

// Modelos auxiliares usados para campos do formulário e em mocks
export type ICharacteristics = Pick<
  IAttribute,
  | "eyeColor"
  | "naturalHairColor"
  | "currentHairColor"
  | "naturalHairType"
  | "currentHairType"
  | "skinColor"
  | "physicalFeatures"
  | "hasTattoo"
>;

export type IMeasurements = Pick<IAttribute, "height" | "shoeSize" | "clothingSize">;

/**
 * Tipo para valores de habilidades que podem ser strings (keys de LanguageEnum) ou objeto (skills)
 */
export type AbilityValue = keyof typeof LanguageEnum | ISkill;
